import { Helmet } from "react-helmet-async";
import {
  buildSeoMeta,
  organizationJsonLd,
  siteName,
  websiteJsonLd,
  type JsonLd,
  type SeoConfig,
} from "@/lib/seo";
import { isGa4Enabled } from "@/lib/ga4";

type Props = SeoConfig;

export function Seo(props: Props) {
  const meta = buildSeoMeta(props);
  const canonical = meta.canonicalUrl;
  const userJsonLd = props.structuredData
    ? Array.isArray(props.structuredData)
      ? props.structuredData
      : [props.structuredData]
    : [];

  const jsonLdItems = [...[organizationJsonLd, websiteJsonLd], ...userJsonLd].reduce<
    JsonLd[]
  >((acc, item) => {
    const key = JSON.stringify(item);
    if (!acc.some((existing) => JSON.stringify(existing) === key)) {
      acc.push(item);
    }
    return acc;
  }, []);

  return (
    <Helmet prioritizeSeoTags>
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <link rel="canonical" href={canonical} />

      <meta name="robots" content={meta.robots} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={meta.ogImage} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.description} />
      <meta name="twitter:image" content={meta.ogImage} />

      {isGa4Enabled && (
        <>
          <link
            rel="preconnect"
            href="https://www.googletagmanager.com"
            crossOrigin="anonymous"
          />
          <link
            rel="dns-prefetch"
            href="https://www.googletagmanager.com"
          />
          <link
            rel="preconnect"
            href="https://www.google-analytics.com"
            crossOrigin="anonymous"
          />
          <link
            rel="dns-prefetch"
            href="https://www.google-analytics.com"
          />
        </>
      )}

      {jsonLdItems.map((data, index) => (
        <script
          key={index}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
        />
      ))}

      {props.children}
    </Helmet>
  );
}

export default Seo;

